import { useMemo } from 'react';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import { DivIcon, LatLngBoundsExpression, latLngBounds } from 'leaflet';
import {
  CLUSTER_STYLE,
  ClusterKind,
  clusterBadgeSize,
} from '../../utils/cluster';

interface ClusterBadgeProps {
  kind: ClusterKind;
  /** How many objects the badge stands for. Always ≥ 2. */
  count: number;
  lat: number;
  lng: number;
  /** Extent of the members — clicking the badge zooms here. */
  bounds: LatLngBoundsExpression;
}

/**
 * A counted badge standing in for map objects too close together to draw
 * separately (issue #434). Clicking it zooms to what it covers.
 *
 * The count carries the information; the kind's colour, glyph and shape only
 * reinforce it, so the badge still reads in greyscale and for colour-blind
 * operators — the same rule the waypoint ramp follows (#341).
 */
export const ClusterBadge: React.FC<ClusterBadgeProps> = ({
  kind,
  count,
  lat,
  lng,
  bounds,
}) => {
  const map = useMap();
  const style = CLUSTER_STYLE[kind];
  const label = `Cluster of ${count} ${style.noun}`;

  const icon = useMemo(() => {
    const size = clusterBadgeSize(count);
    return new DivIcon({
      html: `
        <div
          role="img"
          aria-label="${label}"
          title="${label} — click to zoom in"
          style="
            background: ${style.background};
            color: ${style.foreground};
            border: 2px solid #ffffff;
            border-radius: ${style.round ? '50%' : '8px'};
            width: ${size}px;
            height: ${size}px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            line-height: 1;
            font-weight: bold;
            box-shadow: 0 2px 6px rgba(0,0,0,0.5);
          "
        ><span style="font-size:${Math.round(size * 0.36)}px">${count}</span
        ><span style="font-size:${Math.round(size * 0.26)}px;opacity:0.85">${
          style.glyph
        }</span></div>
      `,
      className: 'planner-cluster-badge',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }, [count, label, style]);

  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      eventHandlers={{
        click: () => {
          // Members can share a position exactly (a stacked pair); fitBounds on
          // a zero-size box would do nothing, so step the zoom instead.
          const box = latLngBounds(
            bounds as [[number, number], [number, number]]
          );
          if (box.getNorth() === box.getSouth() && box.getEast() === box.getWest()) {
            map.setView(box.getCenter(), map.getZoom() + 2);
          } else {
            map.fitBounds(box, { padding: [40, 40] });
          }
        },
      }}
    >
      <Tooltip direction="top" offset={[0, -12]}>
        {label} — click to zoom in
      </Tooltip>
    </Marker>
  );
};
